import {createContext, useContext, useState} from 'react';

import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  AlertTitle,
  Snackbar,
  SnackbarOrigin,
  styled,
} from '@mui/material';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon/FuseSvgIcon';
import {
  ISnackbarProps,
  SnackbarPosition,
  SnackbarType,
} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {COLORS} from '@/styles/colors';

const SnackbarContext = createContext<(props: ISnackbarProps) => void>(() => {
  throw new Error('useSnackbar must be used within a SnackbarProvider');
});

const GlobalIconStyles = styled('div')(() => ({
  '& .MuiSnackbar-root': {
    zIndex: '99999',
  },
  '& .MuiSnackbar-root .MuiSvgIcon-root': {
    fontSize: '28px !important',
  },
}));

const StyledSnackbar = styled(Snackbar)(({type}: {type: SnackbarType}) => {
  let backgroundColor = '';
  let borderColor = '';
  let textColor = '';

  switch (type) {
    case SnackbarType.SUCCESS:
      backgroundColor = COLORS.TOAST_SUCCESS_BACKGROUND;
      borderColor = COLORS.TOAST_SUCCESS_COLOR;
      textColor = COLORS.TOAST_SUCCESS_COLOR;
      break;
    case SnackbarType.ERROR:
      backgroundColor = COLORS.TOAST_ERROR_BACKGROUND;
      borderColor = COLORS.TOAST_ERROR_BORDER;
      textColor = COLORS.TOAST_ERROR_COLOR;
      break;
    case SnackbarType.INFO:
      backgroundColor = COLORS.BACKGROUND_ON_TERTIARY_CONTAINER;
      borderColor = COLORS.ON_TERTIARY_CONTAINER;
      textColor = COLORS.ON_TERTIARY_CONTAINER;
      break;
    case SnackbarType.WARNING:
      backgroundColor = COLORS.TOAST_WARNING_BACKGROUND;
      borderColor = COLORS.TOAST_WARNING_BORDER;
      textColor = COLORS.TOAST_WARNING_COLOR;
      break;
    default:
      backgroundColor = COLORS.WHITE;
      borderColor = COLORS.BLACK;
      textColor = COLORS.BLACK;
      break;
  }

  return {
    '&.MuiSnackbar-anchorOriginTopCenter': {
      background: `${backgroundColor} !important`,
      borderColor: `${borderColor}`,
      color: `${textColor}`,
      borderWidth: '0 0 0 6px',
      borderStyle: 'solid',
      borderRadius: '6px',
      opacity: '1',
      width: '304px',
      transform: 'translate(-50%)',
      left: '50%',
    },
  };
});

const StyledAlert = styled(Alert)(({type}: {type: SnackbarType}) => {
  let iconColor = '';

  switch (type) {
    case SnackbarType.SUCCESS:
      iconColor = '#4CAF50';
      break;
    case SnackbarType.ERROR:
      iconColor = '#73000C';
      break;
    case SnackbarType.INFO:
      iconColor = '#2196F3';
      break;
    case SnackbarType.WARNING:
      iconColor = '#FFEB3B';
      break;
    default:
      iconColor = '#000000';
      break;
  }

  return {
    backgroundColor: 'inherit',
    color: 'inherit',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    '& .MuiAlert-icon': {
      color: `${iconColor}`,
    },
    '& .MuiAlertTitle-root': {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      fontSize: '16px',
      fontWeight: '500',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      maxWidth: '200px',
    },
    '& .MuiAlert-message': {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      textAlign: 'left',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
      letterSpacing: '0.25px',
      maxWidth: '200px',
      gap: '7px',
      display: 'flex',
      flexDirection: 'column',
    },
  };
});

export const showSnackbar = (props: ISnackbarProps) => {
  if (globalShowSnackbar) {
    globalShowSnackbar(props);
  } else {
    console.warn(
      'SnackbarProvider is not mounted. Make sure to wrap your app with SnackbarProvider.',
    );
  }
};

export const useSnackbar = () => useContext(SnackbarContext);

let globalShowSnackbar: (props: ISnackbarProps) => void;

export const SnackbarProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState<ISnackbarProps | null>(null);
  const [open, setOpen] = useState(false);

  const showSnackbar = ({
    summary = 'Error',
    message,
    autoHideDuration = 60000,
    variant = SnackbarType.ERROR,
    position = SnackbarPosition.TOP,
  }: ISnackbarProps) => {
    setSnackbar({summary, message, autoHideDuration, variant, position});
    setOpen(true);
  };

  globalShowSnackbar = showSnackbar;

  const handleClose = () => setOpen(false);

  const getAnchorOrigin = (position: SnackbarPosition): SnackbarOrigin => {
    const positionMap: Record<SnackbarPosition, SnackbarOrigin> = {
      [SnackbarPosition.TOP]: {vertical: 'top', horizontal: 'center'},
      [SnackbarPosition.BOTTOM]: {vertical: 'bottom', horizontal: 'center'},
    };

    return positionMap[position] || {vertical: 'top', horizontal: 'center'};
  };

  const getCustomIcon = (type: SnackbarType): JSX.Element | null => {
    const icons = {
      [SnackbarType.SUCCESS]: (
        <FuseSvgIcon
          sx={{
            color: COLORS.TOAST_SUCCESS_COLOR,
          }}
          size={28}>
          heroicons-outline:check
        </FuseSvgIcon>
      ),
      [SnackbarType.ERROR]: <HighlightOffIcon />,
      [SnackbarType.INFO]: (
        <FuseSvgIcon
          sx={{
            color: COLORS.ON_TERTIARY_CONTAINER,
          }}
          size={28}>
          heroicons-outline:information-circle
        </FuseSvgIcon>
      ),
      [SnackbarType.WARNING]: <WarningIcon />,
    };

    return icons[type] || null;
  };

  return (
    <>
      {children}
      <GlobalIconStyles>
        {snackbar && (
          <StyledSnackbar
            type={snackbar.variant}
            open={open}
            autoHideDuration={snackbar.autoHideDuration}
            onClose={handleClose}
            anchorOrigin={getAnchorOrigin(snackbar.position)}
            classes={{
              root: 'MuiSnackbar-anchorOriginTopCenter',
            }}>
            <StyledAlert
              type={snackbar.variant}
              onClose={handleClose}
              severity={snackbar.variant}
              icon={getCustomIcon(snackbar.variant)}
              sx={{width: '100%', overflowWrap: 'break-word'}}>
              {snackbar.summary && <AlertTitle>{snackbar.summary}</AlertTitle>}
              {snackbar.message}
            </StyledAlert>
          </StyledSnackbar>
        )}
      </GlobalIconStyles>
    </>
  );
};
